import { useEffect } from 'react';
import { debounce } from 'debounce';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import jwt_decode from 'jwt-decode';
import useReduxState from '@hooks/useReduxState';

import { GET_SINGLE_USER } from './graphql/Queries';
import { toastErrorMessage } from '@utils/utils';

import { CoinFlipRedirect } from '@components/coinflip/coinflip-redirect';
import ComingSoon from '@views/comingsoon/ComingSoon';
import CoinFlip from '@views/coinflip';
import Jackpot from '@views/jackpot';
import TermsOfService from '@views/terms';
import ProvablyFair from '@views/provably-fair';
import HelpCenter from '@views/help';
import InGame from '@components/coinflip/ingame/InGame';
import Blackjack from '@views/blackjack';
import BlackjackIngame from '@views/blackjack/ingame';
import User from '@views/user/User';
import useFetchUserTransactions from '@hooks/useFetchUserTransaction';
import useChatEventHandler from '@hooks/useChatEventsHandler';
import HomePage from '@views/home-page';

export default function Main(): JSX.Element {
  const [user, setData] = useReduxState((state) => state.userData);
  const [fetchUser] = useLazyQuery(GET_SINGLE_USER, {
    onCompleted: (data) => {
      setData({
        type: 'SET_USER_DATA',
        arg: data.getSingleUser,
      });
    },
    onError: (error) => {
      toastErrorMessage(error.message);
    },
  });
  const [fetchUserTransactions] = useFetchUserTransactions();

  useChatEventHandler();

  useEffect(() => {
    (async () => {
      // Check for token & check if it's still valid.
      const token = localStorage.getItem('token');

      if (token && user.id === '') {
        const decodedToken = jwt_decode(token);

        // fetch user if existing token hasn't expired yet
        if (decodedToken && new Date().getTime() / 1000 < (decodedToken as any).exp) {
          fetchUser({
            variables: {
              userId: (decodedToken as any).uid,
            },
          });
          fetchUserTransactions((decodedToken as any).id);
        } else {
          setData({
            type: 'RESET_USER_DATA',
            arg: null,
          });
          localStorage.removeItem(token);
        }
      }
    })();
  }, []);

  useEffect(() => {
    const handleResize = debounce(
      () =>
        setData({
          type: 'SET_UI_DATA',
          arg: { windowWidth: window.innerWidth },
        }),
      500
    );

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.clear();
    };
  }, []);

  return (
    <Router>
      <Routes>
        <Route path='/coming-soon' element={<ComingSoon />} />
        <Route path='/' element={<HomePage />} />
        <Route path='/games/coinflip'>
          <Route path='' element={<CoinFlip />} />
          <Route path='/games/coinflip/:gameUid' element={<CoinFlipRedirect />} />
        </Route>
        <Route path='/games/coinflip/:gameId' element={<InGame />} />
        <Route path='/games/jackpot' element={<Jackpot />} />
        <Route path='/games/blackjack'>
          <Route path='' element={<Blackjack />} />
          <Route path='game/:gameUid' element={<BlackjackIngame />} />
        </Route>

        <Route path='/user'>
          <Route path='/user/:userUid' element={<User />} />
        </Route>

        <Route path='/help' element={<HelpCenter />} />
        <Route path='/terms' element={<TermsOfService />} />
        <Route path='/provably-fair' element={<ProvablyFair />} />
      </Routes>
    </Router>
  );
}
